import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/content";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = heroSlides.length;

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const goTo = (next: number) => {
    setIndex((next + total) % total);
  };

  const active = heroSlides[index];

  return (
    <section
      className="relative w-full overflow-hidden rounded-lg bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点内容轮播"
    >
      <div className="relative h-[440px] w-full sm:h-[480px] lg:h-[520px]">
        {heroSlides.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={[
              "absolute inset-0 transition-opacity duration-700",
              slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0",
            ].join(" ")}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={slide.image}
              alt={slide.title}
              title={`${slide.title} - 红桃网焦点推荐`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/55 to-foreground-950/15" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/10 to-transparent" />
          </div>
        ))}

        <div className="relative z-10 flex h-full flex-col justify-end px-5 pb-8 sm:px-8 sm:pb-10 lg:px-12 lg:pb-12">
          <div key={active.id} className="animate-fade-up max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 font-label text-[11px] tracking-wide text-accent-300 backdrop-blur-sm">
              <i className="ri-fire-fill" />
              {active.kicker}
            </span>
            <h2 className="mt-3 font-heading text-2xl font-black leading-tight text-background-50 sm:text-[30px] lg:text-4xl">
              <Link to={active.link} className="transition-colors hover:text-accent-200">
                {active.title}
              </Link>
            </h2>
            <p className="mt-3 hidden max-w-xl text-sm leading-relaxed text-background-100/85 sm:block">
              {active.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={active.link}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {active.cta}
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/news"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                更多资讯
              </Link>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              {heroSlides.map((slide, slideIndex) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => goTo(slideIndex)}
                  aria-label={`切换到第 ${slideIndex + 1} 张焦点图`}
                  aria-current={slideIndex === index}
                  className={[
                    "h-1.5 cursor-pointer rounded-full transition-all duration-300",
                    slideIndex === index ? "w-8 bg-primary-400" : "w-3 bg-background-50/40 hover:bg-background-50/70",
                  ].join(" ")}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-label text-[11px] tracking-widest text-background-100/60">
                {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </span>
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                aria-label="上一张"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                aria-label="下一张"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}